import type {
    GenerationBlockAvgScoreSchema,
    GenerationCriteriaAvgScoreSchema,
    ProductCardLlmBlockTypes,
} from '@/common/api/client/types.gen'
import { DASHBOARD_BLOCK_TYPES, formatScoreValue } from './scoreColors'
import { exportRowsToExcel, type ExcelExportColumn } from './exportExcel'

export const BRAND_COMPARISON_PALETTE = [
    '#8b5cf6',
    '#3b82f6',
    '#08AC81',
    '#f59e0b',
    '#ec4899',
    '#14b8a6',
    '#f97316',
    '#6366f1',
    '#84cc16',
    '#06b6d4',
] as const

export const BLOCK_PROGRESS_COLORS: Record<ProductCardLlmBlockTypes, string> = {
    MAIN_CONTENT: '#8b5cf6',
    ADDITION_VISUAL_ELEMENTS: '#3b82f6',
    DESCRIPTION_CONTENT: '#08ac81',
    TITLE_SCORE: '#f59e0b',
    VISUAL_MATCHING: '#8b5cf6',
    SELLING_POWER: '#8b5cf6',
    DESCRIPTION_ANALYSIS: '#08ac81',
    IMPROVEMENTS: '#f59e0b',
}

export type DetailedComparisonFormat = 'percent' | 'count'

export type DetailedComparisonDetailView = 'blocks' | 'criteria'

export type DetailedBrandBlockData = {
    blockType: ProductCardLlmBlockTypes
    title?: string | null
    maxScore: number
    percent: number
    avgScore: number
    criterias: GenerationCriteriaAvgScoreSchema[]
}

export type DetailedBrandComparisonRow = {
    id: string
    name: string
    color: string
    productCount: number
    overallPercent: number
    overallScore: number
    overallMaxScore: number
    blocks: DetailedBrandBlockData[]
}

export type DetailedBrandOption = {
    id: string
    name: string
    productCount: number
    color: string
}

export const getBrandColor = (index: number): string =>
    BRAND_COMPARISON_PALETTE[index % BRAND_COMPARISON_PALETTE.length]

export const buildDetailedBrandRows = (
    brandOptions: Array<{ id: string; name: string }>,
    commonById: Map<string, { total_products: number; avg_score?: number }>,
    scoresById: Map<string, { avg_score: number; max_score: number; blocks: GenerationBlockAvgScoreSchema[] }>,
    colorById: Map<string, string>
): DetailedBrandComparisonRow[] =>
    brandOptions
        .filter(brand => scoresById.has(brand.id) && commonById.has(brand.id))
        .map(brand => {
            const common = commonById.get(brand.id)!
            const scores = scoresById.get(brand.id)!
            const dashboardBlocks = scores.blocks.filter(b => DASHBOARD_BLOCK_TYPES.includes(b.block_type))

            return {
                id: brand.id,
                name: brand.name,
                color: colorById.get(brand.id) ?? getBrandColor(0),
                productCount: common.total_products,
                overallPercent: scores.max_score > 0 ? (scores.avg_score / scores.max_score) * 100 : 0,
                overallScore: scores.avg_score,
                overallMaxScore: scores.max_score,
                blocks: dashboardBlocks.map(block => ({
                    blockType: block.block_type,
                    title: block.title,
                    maxScore: block.max_score,
                    percent: block.percent ?? 0,
                    avgScore: block.avg_score,
                    criterias: block.criterias ?? [],
                })),
            }
        })

export const getBlockDisplayValue = (
    block: DetailedBrandBlockData,
    format: DetailedComparisonFormat
): { display: string; progressPercent: number } => {
    if (format === 'percent') {
        const percent = Math.round(block.percent)
        return { display: `${percent}%`, progressPercent: block.percent }
    }
    return {
        display: formatScoreValue(block.avgScore),
        progressPercent: block.maxScore > 0 ? (block.avgScore / block.maxScore) * 100 : 0,
    }
}

export const getOverallDisplayValue = (
    row: DetailedBrandComparisonRow,
    format: DetailedComparisonFormat
): { display: string; progressPercent: number } => {
    if (format === 'percent') {
        const percent = Math.round(row.overallPercent)
        return { display: `${percent}%`, progressPercent: row.overallPercent }
    }
    return {
        display: formatScoreValue(row.overallScore),
        progressPercent: row.overallMaxScore > 0 ? (row.overallScore / row.overallMaxScore) * 100 : 0,
    }
}

export const getCriteriaDisplayValue = (
    criteria: GenerationCriteriaAvgScoreSchema,
    format: DetailedComparisonFormat
): { display: string; percent: number } => {
    if (format === 'percent') {
        const percent = Math.round(criteria.percent)
        return { display: `${percent}%`, percent: criteria.percent }
    }
    const count = criteria.count ?? 0
    return { display: String(count), percent: criteria.percent }
}

export const sortBrandsByProductCount = (options: DetailedBrandOption[]): DetailedBrandOption[] =>
    [...options].sort((a, b) => b.productCount - a.productCount)

export const getTopBrandsByVolume = (options: DetailedBrandOption[], limit = 10): string[] =>
    sortBrandsByProductCount(options)
        .slice(0, limit)
        .map(brand => brand.id)

export type ExcelColumn = ExcelExportColumn<DetailedBrandComparisonRow>

export const exportDetailedComparisonToExcel = (
    rows: DetailedBrandComparisonRow[],
    columns: ExcelColumn[],
    filename: string
): void => {
    exportRowsToExcel(rows, columns, filename, 'Сравнение')
}
